import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const navLinks = [
    { path: '/', label: '首页', icon: 'ri-home-line' },
    { path: '/category/tv-series', label: '电视剧', icon: 'ri-tv-line' },
    { path: '/category/movies', label: '电影', icon: 'ri-film-line' },
    { path: '/category/anime', label: '动漫', icon: 'ri-rocket-line' },
    { path: '/category/variety', label: '综艺', icon: 'ri-live-line' },
    { path: '/about', label: '关于我们', icon: 'ri-information-line' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-2xl font-bold font-heading text-accent-600 whitespace-nowrap">
              密桃视频
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  (link.path === '/' ? location.pathname === '/' : location.pathname.startsWith(link.path))
                    ? 'text-accent-600 bg-accent-100/70'
                    : 'text-foreground-600 hover:text-accent-600 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Search Bar + Mobile Menu Button */}
          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="hidden sm:flex items-center">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索影片..."
                  className="w-48 lg:w-64 h-9 pl-9 pr-3 rounded-full text-sm bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-accent-300/60 transition-colors"
                />
                <button type="submit" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-accent-500 transition-colors cursor-pointer">
                  <i className="ri-search-line text-sm w-4 h-4 flex items-center justify-center" />
                </button>
              </div>
            </form>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="菜单"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg w-5 h-5 flex items-center justify-center`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70">
          <div className="px-4 py-3">
            <form onSubmit={handleSearch} className="mb-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索影片..."
                  className="w-full h-10 pl-10 pr-3 rounded-full text-sm bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-accent-300/60"
                />
                <button type="submit" className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 cursor-pointer">
                  <i className="ri-search-line w-4 h-4 flex items-center justify-center" />
                </button>
              </div>
            </form>
            <div className="space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    (link.path === '/' ? location.pathname === '/' : location.pathname.startsWith(link.path))
                      ? 'text-accent-600 bg-accent-100/70'
                      : 'text-foreground-600 hover:text-accent-600 hover:bg-background-100'
                  }`}
                >
                  <i className={`${link.icon} w-4 h-4 flex items-center justify-center`} />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}